import { FC, useMemo, useRef } from "react";
import {
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
  MuiLink,
  MuiMultiSelect,
  MuiTypography,
} from "../../../../../../../../../../../components";
import MuiChip from "../../../../../../../../../../../components/chip/MuiChip";
import { MuiDoubleLabelWithChip } from "../../../../../../../../../../../components/doubleLabelWithChip/MuiDoubleLabelWithChip";
import {
  StatusBadge,
  StatusBadgeType,
} from "../../../../../../../../../../../components/statusBadge/StatusBadge";
import { EMPTY_VALUE_DASH } from "../../../../../../../../../../../helpers/constants";
import { isNotEmpty } from "../../../../../../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../../../../../../hooks/useGridContainer";
import { useMultiSelectARIALabels } from "../../../../../../../../../../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../../../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../../../../hooks/useSiteCore";
import { addActivitySiteCoreKeys } from "../../../siteCore/SiteCoreKeys";
import { TopicDetails } from "../../../../../../../../../../../api/services/project/model";

interface ProjectViewActivityProps {
  projectActivityData: TopicDetails;
  children?: React.ReactNode;
  onClickViewManageLocal: () => void;
}

export const ProjectViewActivityGeneral: FC<ProjectViewActivityProps> = ({
  projectActivityData,
  children,
  onClickViewManageLocal,
}) => {
  const candidateStatusRef = useRef<HTMLDivElement>(null);
  const modalityRef = useRef<HTMLDivElement>(null);
  const { gridSizeMedium: gridSize, gridSizeMediumClass } =
    useGridContainer(true);
  const { fetchValueByKeyOfDropdown, fetchLabel, commonLabels } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const multiSelectARIALabels = useMultiSelectARIALabels();
  const activityStatusLabel = fetchValueByKeyOfDropdown(
    addActivitySiteCoreKeys.addActivity,
    addActivitySiteCoreKeys.statusList,
    projectActivityData?.activityStatusId ?? "",
  );

  const renderOptionChip = (value: string) => {
    return (
      <MuiChip
        data={[
          {
            value: value,
            label: fetchLabel(addActivitySiteCoreKeys.addActivity, value),
          },
        ]}
        handleDelete={() => {}}
        isEditable={false}
        removeChipNameARIALabel={commonARIALabel.removeChipName}
        showMoreARIALabel={commonARIALabel.showNMore}
      />
    );
  };

  const projectDeliverablesOption = useMemo(() => {
    return Array.isArray(projectActivityData?.deliverables)
      ? projectActivityData.deliverables.map((item) => ({
          label: item.name,
          value: item.name,
        }))
      : [];
  }, [projectActivityData]);

  const projectModalityChipsValue = useMemo(() => {
    if (!projectActivityData?.modalityIds?.length) {
      return [];
    }
    return projectActivityData.modalityIds.map(
      (modalitySiteCoreValue: string) => ({
        label: fetchValueByKeyOfDropdown(
          addActivitySiteCoreKeys.addActivity,
          addActivitySiteCoreKeys.modalityList,
          modalitySiteCoreValue,
        ),
        value: modalitySiteCoreValue,
      }),
    );
  }, [projectActivityData?.modalityIds]);

  return (
    <MuiBox>
      <MuiGrid
        container
        data-testid="activity-tab-general"
        className="activity-tab-general"
      >
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={fetchLabel(
              addActivitySiteCoreKeys.addActivity,
              addActivitySiteCoreKeys.activityType,
            )}
            value={projectActivityData?.activityType?.name ?? ""}
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabelWithChip
            label={fetchLabel(
              addActivitySiteCoreKeys.addActivity,
              addActivitySiteCoreKeys.modality,
            )}
            replaceEmptyWithDash={!projectActivityData?.modalityIds?.length}
            value={projectModalityChipsValue}
            chipPopoverGridRef={modalityRef}
            chipPopoverHeaderLabel={fetchLabel(
              addActivitySiteCoreKeys.addActivity,
              addActivitySiteCoreKeys.modality,
            )}
            showMoreARIALabel={commonARIALabel.showNMore}
            removeChipNameARIALabel={commonARIALabel.removeChipName}
            closePopoverARIALabel={commonARIALabel.closePopover}
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={fetchLabel(
              addActivitySiteCoreKeys.addActivity,
              addActivitySiteCoreKeys.topic,
            )}
            value={projectActivityData?.topic.name ?? ""}
            isValueWithEllipsis
            valueClasses="topic-value"
          />
          <MuiLink
            className="view-locale-link"
            onClick={onClickViewManageLocal}
            data-testid="view-manage-local-link"
          >
            {commonLabels.viewLocale}
          </MuiLink>
        </MuiGrid>
        <MuiGrid size={gridSize} className="deliverables-dropdown">
          <MuiDoubleLabel
            label={fetchLabel(
              addActivitySiteCoreKeys.addActivity,
              addActivitySiteCoreKeys.deliverables,
            )}
            value={
              isNotEmpty(projectActivityData?.deliverables) &&
              projectDeliverablesOption.length
                ? ""
                : EMPTY_VALUE_DASH
            }
          />
          {isNotEmpty(projectActivityData?.deliverables) &&
            !!projectDeliverablesOption.length && (
              <MuiMultiSelect
                label={""}
                options={projectDeliverablesOption}
                showChip={true}
                showDropdown={false}
                isChipEditable={false}
                dialogTitle={fetchLabel(
                  addActivitySiteCoreKeys.addActivity,
                  addActivitySiteCoreKeys.deliverables,
                )}
                dialogWidth={376}
                selectedValues={
                  projectActivityData?.deliverables
                    ? projectActivityData.deliverables.map((item) => item.name)
                    : []
                }
                selectPlaceHolder={commonLabels.selectPlaceHolder}
                searchPlaceHolder={commonLabels.search}
                selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                doneText={commonLabels.done}
                allARIAProps={multiSelectARIALabels}
              />
            )}
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={fetchLabel(
              addActivitySiteCoreKeys.addActivity,
              addActivitySiteCoreKeys.activityStatus,
            )}
            value={""}
          />
          <StatusBadge
            status={projectActivityData?.activityStatusId as StatusBadgeType}
            label={activityStatusLabel}
          />
        </MuiGrid>
      </MuiGrid>

      <MuiBox>
        <MuiTypography
          variant="h2Bold"
          className="select-applicable"
          component="h2"
        >
          {fetchLabel(
            addActivitySiteCoreKeys.addActivity,
            addActivitySiteCoreKeys.defaultActivityValues,
          )}
        </MuiTypography>
      </MuiBox>
      {children}

      {(projectActivityData?.triggerBilling ||
        projectActivityData?.countsTowardsLastContactDate ||
        projectActivityData?.triggerTouchPointSurvey) && (
        <>
          <MuiBox>
            <MuiTypography
              variant="h2Bold"
              className="select-applicable"
              component="h2"
            >
              {fetchLabel(
                addActivitySiteCoreKeys.addActivity,
                addActivitySiteCoreKeys.selectedPreferencesHeading,
              )}
            </MuiTypography>
          </MuiBox>
          <MuiBox className="checkbox-container">
            {projectActivityData?.triggerBilling &&
              renderOptionChip(addActivitySiteCoreKeys.triggerBilling)}
            {projectActivityData?.countsTowardsLastContactDate &&
              renderOptionChip(
                addActivitySiteCoreKeys.countsTowardsLastContactDate,
              )}
            {projectActivityData?.triggerTouchPointSurvey &&
              renderOptionChip(addActivitySiteCoreKeys.triggerTouchPointSurvey)}
          </MuiBox>
        </>
      )}
    </MuiBox>
  );
};
